import { FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'hotel/navigation';

import { PrimaryButton, SecondaryButton } from 'components/Button';
import Loading from 'components/Loading';
import { Amenity, HotelChainGroup } from 'generated/graphql';
import { useAppSelector } from 'store/store';

import FilterMenu from './FilterMenu';
import HotelSearchResult from './HotelSearchResult';

import { HotelSearchContext } from '.';

export type HotelSearchFilters = {
  radius?: number;
  amenities?: Amenity[];
  hotelChainGroup?: HotelChainGroup;
  accessibleRoomsOnly?: boolean;
};

const HotelSearchResultList: React.FC = () => {
  const {
    data,
    loading,
    handleFetchMore,
    handleHotelSelect,
    handleRefetch,
    highlightHotel,
    highlightedHotelId,
  } = useOutletContext<HotelSearchContext>();
  const bookingType = useAppSelector((state) => state.booking.bookingType);

  const [filters, setFilters] = useState<HotelSearchFilters>({
    amenities: [],
    hotelChainGroup: undefined,
    radius: 1,
    accessibleRoomsOnly: false,
  });

  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const handleFilterChange = (newFilters: HotelSearchFilters) => {
    const nextFilters = {
      ...filters,
      ...newFilters,
    };

    setFilters(nextFilters);

    if (
      newFilters.radius !== undefined ||
      newFilters.accessibleRoomsOnly !== undefined
    ) {
      handleRefetch({
        radius: nextFilters.radius ?? 1,
        accessibleRoomsOnly: nextFilters.accessibleRoomsOnly ?? false,
      });
    }

    // Close mobile filter modal when a filter is applied
    setIsMobileFilterOpen(false);
  };

  const filteredResults = useMemo(() => {
    if (!data?.hotelSearch?.results) return [];

    let filteredResults = data.hotelSearch.results;

    const localAmenityFilters = filters.amenities?.filter(
      (amenity) => amenity !== Amenity.AdaAccommodation,
    );

    if (localAmenityFilters?.length) {
      filteredResults = filteredResults.filter((result) =>
        localAmenityFilters.every((amenity) =>
          result.hotel.amenities.includes(amenity),
        ),
      );
    }

    if (filters.hotelChainGroup) {
      filteredResults = filteredResults.filter(
        (result) =>
          !filters.hotelChainGroup?.code ||
          result.hotel.hotelChainGroup?.code === filters.hotelChainGroup?.code,
      );
    }
    return filteredResults;
  }, [data, filters]);

  // Scroll to highlighted hotel when it changes
  useEffect(() => {
    if (highlightedHotelId) {
      // Small delay to ensure DOM is updated
      const timeoutId = setTimeout(() => {
        const element = document.getElementById(`hotel-${highlightedHotelId}`);
        if (element) {
          try {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Add focus for accessibility
            const focusableElement = element.querySelector(
              'button, a, [tabindex]',
            ) as HTMLElement;
            if (focusableElement) {
              focusableElement.focus();
            }
          } catch (error) {
            console.error('Failed to scroll to hotel:', error);
          }
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [highlightedHotelId]);

  return (
    <div className="flex h-full flex-col lg:flex-row">
      {/* Mobile Filter Button */}
      <div className="flex items-center justify-between border-b border-gray-200 p-4 lg:hidden">
        <p className="font-oswald text-base font-bold text-primaryGray">
          RESULTS{' '}
          {filteredResults?.length > 0 && `(${filteredResults?.length} NEARBY)`}
        </p>
        <SecondaryButton
          onClick={() => setIsMobileFilterOpen(true)}
          className="flex items-center gap-2"
        >
          <FunnelIcon className="size-4" />
          Filters
        </SecondaryButton>
      </div>

      {/* Desktop Filter Sidebar */}
      <div className="hidden flex-[2.5] border-r border-gray-200 lg:flex">
        <div className="flex min-h-16 w-full flex-col items-start p-4">
          <p className="mb-5 font-oswald text-base font-bold text-primaryGray">
            FILTER BY:
          </p>
          <FilterMenu onFilterChange={handleFilterChange} filters={filters} />
        </div>
      </div>

      {/* Mobile Filter Modal/Drawer */}
      {isMobileFilterOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Backdrop */}
          <button
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsMobileFilterOpen(false)}
            aria-label="Close filters"
          />
          {/* Modal */}
          <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-lg bg-white">
            <div className="flex items-center justify-between border-b border-gray-200 p-4">
              <h2 className="font-oswald text-lg font-bold text-primaryGray">
                FILTER BY:
              </h2>
              <button
                onClick={() => setIsMobileFilterOpen(false)}
                className="rounded-md p-2 hover:bg-gray-100"
              >
                <XMarkIcon className="size-5" />
              </button>
            </div>
            <div className="p-4">
              <FilterMenu
                onFilterChange={handleFilterChange}
                filters={filters}
              />
            </div>
          </div>
        </div>
      )}

      {/* Results Section */}
      <div className="scrollbar-hide flex-1 lg:h-full lg:flex-[7.5] lg:overflow-y-scroll">
        {/* Desktop Results Header */}
        <div className="hidden min-h-16 items-center border-b border-gray-200 p-4 lg:flex">
          <p className="font-oswald text-base font-bold text-primaryGray">
            RESULTS{' '}
            {filteredResults?.length > 0 &&
              `(${filteredResults?.length} NEARBY)`}
          </p>
        </div>

        <div className="pb-32">
          {loading && !data?.hotelSearch.moreIndicator ? (
            <Loading className="my-2" />
          ) : filteredResults?.length > 0 ? (
            <>
              {filteredResults.map((result, index) => (
                <div key={index} id={`hotel-${result.hotel.amadeusHotelId}`}>
                  <HotelSearchResult
                    result={result}
                    bookingType={bookingType}
                    onClick={() =>
                      handleHotelSelect && handleHotelSelect(result)
                    }
                    onMouseEnter={() =>
                      highlightHotel(result.hotel.amadeusHotelId)
                    }
                    onMouseLeave={() => highlightHotel(null)}
                    isHighlighted={
                      highlightedHotelId === result.hotel.amadeusHotelId
                    }
                  />
                </div>
              ))}
              {loading ? (
                <Loading className="my-2" />
              ) : (
                data?.hotelSearch.moreIndicator && (
                  <div className="flex items-center justify-center">
                    <PrimaryButton onClick={handleFetchMore} className="my-2">
                      Load More
                    </PrimaryButton>
                  </div>
                )
              )}
            </>
          ) : (
            <div className="flex h-40 items-center justify-center">
              <p className="text-lg text-primaryGray">No results found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HotelSearchResultList;
